import { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import styles from "./Institution.module.scss";
import InstitutionConfirmation from "./InstitutionConfirmation";
import ProductSelector from "./ProductSelector";
import ContactForm from "./ContactForm";
import InstitutionDetailsSection from "./InstitutionDetailsSection";
import useToast from "../../../hooks/useToast";
import useSalesforce from "../../../hooks/useSalesforce.js";
import { apiService } from "../../../api/apiAdapter";
import {
  mapUrlReferralsToFrontend,
  buildSubmissionPayload,
  validateInstitutionForm,
} from "./institutionFormUtils";

const INITIAL_FORM_STATE = {
  selectedProducts: [],
  firstName: "",
  lastName: "",
  email: "",
  organisation: "",
  abn: "",
  contactNumber: "",
  isAccountAdmin: null,
  isSameUserBase: null,
  fteNumber: "",
  authenticationType: [],
  ipAddress: "",
  urlReferrals: {
    tg: "",
    obesid: "",
    drtc: "",
  },
  openAthensId: "",
  comment: "",
};

const Institution = ({ type = "new", onBack, onSubmit, onDataLoaded: _onDataLoaded }) => {
  const { triggerToast } = useToast();
  const [searchParams] = useSearchParams();
  const recordId = searchParams.get("id");
  const hasFetchedData = useRef(false);
  const { isSalesforceEnabled } = useSalesforce();
  const [isLoading, setIsLoading] = useState(!!recordId);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Use local state for institution type when loading from recordId
  const [loadedInstitutionType, setLoadedInstitutionType] = useState(null);
  const isExisting =
    recordId && loadedInstitutionType ? loadedInstitutionType === "existing" : type === "existing";

  // Auto-populate form when recordId is provided in URL
  useEffect(() => {
    if (!recordId || hasFetchedData.current) return;

    hasFetchedData.current = true;

    const fetchEnquiryInstitutionData = async () => {
      try {
        const response = await apiService.getInstitutionEnquiryById(recordId, isSalesforceEnabled);

        if (response.success && response.data) {
          const data = response.data;

          // Parse URL referrals from separate Dynamics fields using utility
          const urlReferralsObj = mapUrlReferralsToFrontend(data);

          // Set the institution type from loaded data
          if (data.institutionType) {
            setLoadedInstitutionType(data.institutionType);
          }

          setFormData({
            selectedProducts: data.subscriptions || [],
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            organisation: data.organisation || "",
            abn: data.abn || "",
            contactNumber: data.contactNumber || "",
            isAccountAdmin: data.isAccountAdmin !== undefined ? data.isAccountAdmin : null,
            isSameUserBase: data.isSameUserBase !== undefined ? data.isSameUserBase : null,
            fteNumber: data.fteNumber || "",
            authenticationType: data.authenticationType || [],
            ipAddress: data.ipAddress || "",
            urlReferrals: urlReferralsObj,
            openAthensId: data.openAthensId || "",
            comment: data.comment || "",
          });
        }
      } catch (error) {
        console.error("Error fetching enquiry institution data:", error.message);
        triggerToast({
          type: "error",
          primaryMessage: "Failed to load enquiry institution data. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEnquiryInstitutionData();
  }, [recordId]);

  const handleProductToggle = (productId) => {
    setFormData((prev) => ({
      ...prev,
      selectedProducts: prev.selectedProducts.includes(productId)
        ? prev.selectedProducts.filter((id) => id !== productId)
        : [...prev.selectedProducts, productId],
    }));
    if (errorMessage) setErrorMessage("");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage("");
  };

  const handleAuthenticationToggle = (type) => {
    setFormData((prev) => ({
      ...prev,
      authenticationType: prev.authenticationType.includes(type)
        ? prev.authenticationType.filter((t) => t !== type)
        : [...prev.authenticationType, type],
    }));
    if (errorMessage) setErrorMessage("");
  };

  const handleUrlReferralChange = (productId, value) => {
    setFormData((prev) => ({
      ...prev,
      urlReferrals: {
        ...prev.urlReferrals,
        [productId]: value,
      },
    }));
  };

  const clearConfigurationFields = () => {
    setFormData((prev) => ({
      ...prev,
      fteNumber: "",
      authenticationType: [],
      ipAddress: "",
      urlReferrals: { tg: "", obesid: "", drtc: "" },
      openAthensId: "",
      comment: "",
    }));
  };
  const handleConfigurationChange = (value) => {
    const fieldName = isExisting ? "isSameUserBase" : "isAccountAdmin";
    const currentValue = formData[fieldName];

    // Allow deselecting by clicking the same radio button again
    if (currentValue === value) {
      handleInputChange(fieldName, null);
      clearConfigurationFields();
      return;
    }

    // Set the value first
    handleInputChange(fieldName, value);

    // Clear configuration fields based on the selection
    // For new institutions: clear fields when selecting "No" to show confirmation
    // For existing institutions: keep fields when switching between Yes/No
    if (!isExisting && value === false) {
      clearConfigurationFields();
    }

    // Show confirmation modal if "No" was selected (only for new institution)
    if (!isExisting && value === false) {
      setShowConfirmation(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Build submission payload using utility function
      const institutionData = buildSubmissionPayload(
        formData,
        isExisting,
        shouldShowFTE,
        shouldShowDetailsSection,
      );

      // If recordId exists, update existing enquiry; otherwise create new one
      const response = await apiService.createOrUpdateInstitutionEnquiry(
        institutionData,
        recordId,
        isSalesforceEnabled,
      );

      if (response.success) {
        setErrorMessage("");

        // Show success message
        const successMessage = recordId
          ? "Institution details updated successfully."
          : "Subscription request sent successfully.";

        if (!recordId && response.editLink) {
          // Only show edit link message for new submissions
          triggerToast({
            type: "success",
            primaryMessage:
              successMessage +
              " You can edit your request anytime using the link sent to your email.",
          });
        } else {
          triggerToast({ type: "success", primaryMessage: successMessage });
        }

        if (onSubmit) onSubmit(formData);
      }
    } catch (error) {
      const errMsg = "Failed to send enquiry request. Please try again.";

      try {
        const parsedError = JSON.parse(error.message);
        if (parsedError?.errorCode === "DUPLICATES_DETECTED") {
          const errorMsg =
            "A similar Enquiry request already exists. Please check your email for details or contact support.";
          setErrorMessage(errorMsg);
          triggerToast({
            type: "error",
            primaryMessage: errorMsg,
          });
          return;
        }
      } catch {
        // console.error(`Error parsing  ${errMsg}`);
      }
      setErrorMessage(errMsg);
      triggerToast({
        type: "error",
        primaryMessage: errMsg,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    // Use validation utility function
    const result = validateInstitutionForm(formData, isExisting, shouldValidateAuth);

    if (!result.isValid) {
      setErrorMessage(result.errorMessage);
      return false;
    }

    setErrorMessage("");
    return true;
  };

  // Computed values for conditional logic using useMemo for performance
  const shouldShowDetailsSection = useMemo(() => {
    if (!isExisting) {
      // For new institution, only show details section if isAccountAdmin is true
      return formData.isAccountAdmin === true;
    }
    // For existing, show if isSameUserBase is answered
    return formData.isSameUserBase !== null;
  }, [isExisting, formData.isSameUserBase, formData.isAccountAdmin]);

  const shouldShowFTE = useMemo(
    () => (isExisting ? formData.isSameUserBase === false : formData.isAccountAdmin === true),
    [isExisting, formData.isSameUserBase, formData.isAccountAdmin],
  );

  const shouldValidateAuth = shouldShowDetailsSection;

  return (
    <>
      {isLoading ? (
        <div className={styles.newInstitutionWrapper}>
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading institution data...</p>
          </div>
        </div>
      ) : (
        <div className={styles.container}>
          {onBack && (
            <button
              type="button"
              variant="secondary"
              onClick={onBack}
              className={styles.backButton}
            >
              <i className="tg-icons-RightArrow" /> Back
            </button>
          )}

          <div className={styles.headerSection}>
            <h1 className={styles.title}>Access request form for institutional customers</h1>
            <p className={styles.description}>
              {isExisting
                ? "This form is for existing Therapeutic Guidelines customers. Complete the form below to request access to another product or to update your institutional details."
                : "Set up a new subscription for your organisation. Complete the form below to get started."}
            </p>
          </div>
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Product Selection */}
            <ProductSelector
              selectedProducts={formData.selectedProducts}
              onProductToggle={handleProductToggle}
              isExisting={isExisting}
            />

            {/* Contact Details */}
            <ContactForm formData={formData} onInputChange={handleInputChange} />

            {/* Institution Details Section */}
            <InstitutionDetailsSection
              isExisting={isExisting}
              formData={formData}
              selectedProducts={formData.selectedProducts}
              shouldShowDetailsSection={shouldShowDetailsSection}
              shouldShowFTE={shouldShowFTE}
              onConfigurationChange={handleConfigurationChange}
              onInputChange={handleInputChange}
              onAuthenticationToggle={handleAuthenticationToggle}
              onUrlReferralChange={handleUrlReferralChange}
            />

            {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}

            <div className={styles.actions}>
              <button
                type="submit"
                variant="primary"
                className={styles.submitButton}
                disabled={isSubmitting || (!isExisting && formData.isAccountAdmin === false)}
              >
                {isSubmitting
                  ? recordId
                    ? "Updating..."
                    : "Submitting..."
                  : recordId
                    ? "Update Enquiry"
                    : "Submit Enquiry"}
              </button>
              <p className={styles.actionNote}>
                To speak to one of our Sales Team about your subscription options, email{" "}
                <a href="mailto:sales@tg.org.au" className={styles.emailLink}>
                  sales@tg.org.au
                </a>{" "}
                or call 1800 061 260.
              </p>
            </div>
          </form>
        </div>
      )}

      {!isExisting && (
        <InstitutionConfirmation
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
        />
      )}
    </>
  );
};

Institution.propTypes = {
  type: PropTypes.oneOf(["new", "existing"]),
  onBack: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onDataLoaded: PropTypes.func, // Optional - checked before use
};

export default Institution;
